import posthog from "posthog-js";
import { isElectronRuntime } from "./logout";

const POSTHOG_HOST_DEFAULT = "https://eu.i.posthog.com";

const apiKey =
  (import.meta.env.VITE_POSTHOG_KEY as string | undefined)?.trim() ?? "";
const host =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined)?.trim() ||
  POSTHOG_HOST_DEFAULT;

let initialized = false;

function resolvePostHogEnvironment(): string {
  if (import.meta.env.MODE === "development") return "development";
  const buildTarget = import.meta.env.VITE_BUILD_TARGET as string | undefined;
  return buildTarget === "production" ? "production" : "staging";
}

export function initPostHog(): void {
  if (initialized || !apiKey) return;
  const appEnv = resolvePostHogEnvironment();
  // PostHog is production-only by policy. Staging + dev are silent.
  if (appEnv !== "production") return;
  initialized = true;

  posthog.init(apiKey, {
    api_host: host,
    capture_pageview: "history_change",
    capture_pageleave: true,
    capture_performance: { web_vitals: true },
    autocapture: true,
    enable_heatmaps: true,
    // "always" (not "identified_only"): the public community pages are reached
    // by logged-OUT "lurkers" in the browser. "always" assigns each one an
    // anonymous distinct_id and PostHog stitches it to the real person on
    // `identify()` at signup — so the lurk → sign up → write funnel connects
    // per person instead of collapsing into anonymous event counts.
    person_profiles: "always",
    loaded: () => {
      if (import.meta.env.DEV) {
        posthog.debug();
      }
    },
  });
  posthog.register({
    app_env: appEnv,
    deployment_environment: appEnv,
    app_surface: isElectronRuntime() ? "desktop" : "web",
  });
}

export function identifyPostHogUser(opts: {
  id: string;
  email?: string | null;
  name?: string | null;
}): void {
  if (!initialized) return;
  posthog.identify(opts.id, {
    ...(opts.email ? { email: opts.email } : {}),
    ...(opts.name ? { name: opts.name } : {}),
  });
}

export function resetPostHog(): void {
  if (!initialized) return;
  posthog.reset();
}

/**
 * Bind subsequent events to a workspace "group" so client-side events roll up
 * to the same `workspace` group the server already populates via
 * `identifyWorkspaceGroup`. On the public pages this groups a lurker's events
 * by the community they're viewing — the basis for "which community converts
 * visitors". No-op until PostHog is initialized.
 */
export function setPostHogGroup(workspaceId: string): void {
  if (!initialized || !workspaceId) return;
  posthog.group("workspace", workspaceId);
}

/**
 * Drop the current workspace-group association. Called when a user
 * authenticates (current-user.tsx) so the anonymous-lurker community group
 * bound by `setPostHogGroup` on a public page doesn't bleed into their own
 * authenticated events. No-op until PostHog is initialized.
 */
export function resetPostHogGroups(): void {
  if (!initialized) return;
  posthog.resetGroups();
}

/**
 * Capture a custom product-analytics event. No-ops unless PostHog is
 * initialized (production only, per `initPostHog`), so staging/dev stay
 * silent. Use for low-frequency signals worth counting in aggregate
 * (e.g. Zero connection-error episodes per deploy).
 */
export function capturePostHogEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (!initialized) return;
  posthog.capture(event, properties);
}
